import React, { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Upload,
  Download,
  FileSpreadsheet,
  X,
  CheckCircle2,
  AlertOctagon,
  RefreshCcw,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, API_BASE } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { cn } from "@/lib/utils";

const ACCEPT = ".csv,.xlsx";
const MAX_BYTES = 5 * 1024 * 1024;

export default function ImportClientsModal({ open, onOpenChange }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [generateCompliances, setGenerateCompliances] = useState(true);
  const [generationMonths, setGenerationMonths] = useState(12);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setDrag(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFile = (f) => {
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!(lower.endsWith(".csv") || lower.endsWith(".xlsx"))) {
      toast.error("Only .csv and .xlsx files are supported");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("File exceeds 5 MB limit");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const importM = useMutation({
    mutationFn: () =>
      api.importClients({
        file,
        generateCompliances,
        generationMonths: Number(generationMonths),
        skipDuplicates,
      }),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ["clients"] });
      const created = r.createdCount || 0;
      if (created > 0) toast.success(`${created} client${created === 1 ? "" : "s"} imported`);
      else toast.info("No clients were created — review the summary below");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Import failed"),
  });

  const canSubmit = !!file && !importM.isPending;

  const templateHref = api.importClientsTemplateUrl();

  const handleClose = (o) => {
    if (importM.isPending) return;
    onOpenChange(o);
    if (!o) setTimeout(reset, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-xl p-0 gap-0 border-[#E2E8F0]"
        data-testid={TID.importModal}
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E8F0]">
          <DialogTitle className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-[#64748B]" />
            Bulk Import Clients
          </DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Upload a CSV or Excel file with your client roster. Each row becomes
            a client and (optionally) receives its applicable statutory compliance
            pack instantly.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 flex flex-col gap-4 max-h-[65vh] overflow-y-auto">
          {!result && (
            <>
              <div className="flex items-center justify-between gap-2 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <div>
                  <div className="text-xs font-medium text-[#334155]">
                    Not sure about the format?
                  </div>
                  <div className="text-[11px] text-[#64748B]">
                    Download our CSV template with example rows.
                  </div>
                </div>
                <a
                  href={templateHref}
                  download
                  data-testid={TID.importDownloadTemplate}
                  className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-[#E2E8F0] bg-white text-xs font-medium text-[#334155] hover:bg-[#F8FAFC]"
                >
                  <Download className="h-3.5 w-3.5" />
                  Template
                </a>
              </div>

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  const f = e.dataTransfer?.files?.[0];
                  if (f) handleFile(f);
                }}
                onClick={() => inputRef.current?.click()}
                data-testid={TID.importFileDrop}
                className={cn(
                  "rounded-md border-2 border-dashed p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors",
                  drag
                    ? "border-[#0F172A] bg-[#F1F5F9]"
                    : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                )}
              >
                <Upload className="h-6 w-6 text-[#64748B]" />
                <div className="text-sm font-medium text-[#0F172A]">
                  {file ? file.name : "Click or drop a .csv / .xlsx file"}
                </div>
                <div className="text-[11px] text-[#64748B]">
                  Max 5 MB · up to 500 rows per import
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid={TID.importFileInput}
                />
              </div>

              <div className="rounded-md border border-[#E2E8F0] p-3 grid gap-3">
                <label className="inline-flex items-center gap-2 text-xs text-[#334155]">
                  <input
                    type="checkbox"
                    checked={generateCompliances}
                    onChange={(e) => setGenerateCompliances(e.target.checked)}
                    className="h-3.5 w-3.5 accent-[#0F172A]"
                    data-testid={TID.importGenerate}
                  />
                  <span className="font-medium">
                    Auto-generate applicable compliances for each imported client
                  </span>
                </label>
                {generateCompliances && (
                  <div className="flex items-center gap-2 pl-6">
                    <span className="text-xs text-[#64748B]">
                      Horizon (months)
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={36}
                      value={generationMonths}
                      onChange={(e) => setGenerationMonths(e.target.value)}
                      className="w-20 h-8 px-2 rounded-md border border-[#E2E8F0] bg-white text-sm"
                      data-testid={TID.importMonths}
                    />
                  </div>
                )}
                <label className="inline-flex items-center gap-2 text-xs text-[#334155]">
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                    className="h-3.5 w-3.5 accent-[#0F172A]"
                    data-testid={TID.importSkipDup}
                  />
                  <span className="font-medium">
                    Skip rows whose Name already exists
                  </span>
                </label>
              </div>
            </>
          )}

          {result && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-3 gap-2">
                <ResultChip
                  icon={CheckCircle2}
                  label="Created"
                  value={result.createdCount}
                  accent={{ bg: "#DCFCE7", fg: "#166534" }}
                  testId={TID.importResultCreated}
                />
                <ResultChip
                  icon={RefreshCcw}
                  label="Skipped"
                  value={result.skippedCount}
                  accent={{ bg: "#F1F5F9", fg: "#334155" }}
                  testId={TID.importResultSkipped}
                />
                <ResultChip
                  icon={AlertOctagon}
                  label="Failed"
                  value={result.failedCount}
                  accent={{ bg: "#FEE2E2", fg: "#991B1B" }}
                  testId={TID.importResultFailed}
                />
              </div>

              {result.failed?.length > 0 && (
                <div className="rounded-md border border-[#FECACA] bg-[#FEF2F2] p-3">
                  <div className="text-xs font-semibold text-[#991B1B] mb-1.5">
                    Failed rows
                  </div>
                  <ul className="text-[11px] text-[#7F1D1D] space-y-0.5 max-h-32 overflow-y-auto">
                    {result.failed.map((f, i) => (
                      <li key={i}>
                        Row {f.row}
                        {f.name ? ` (${f.name})` : ""}: {f.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.skipped?.length > 0 && (
                <div className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                  <div className="text-xs font-semibold text-[#334155] mb-1.5">
                    Skipped (duplicates)
                  </div>
                  <ul className="text-[11px] text-[#64748B] space-y-0.5 max-h-32 overflow-y-auto">
                    {result.skipped.map((s, i) => (
                      <li key={i}>
                        Row {s.row}: {s.name} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.created?.length > 0 && (
                <div className="rounded-md border border-[#BBF7D0] bg-[#F0FDF4] p-3">
                  <div className="text-xs font-semibold text-[#166534] mb-1.5">
                    Created ({result.created.length})
                  </div>
                  <ul className="text-[11px] text-[#065F46] space-y-0.5 max-h-40 overflow-y-auto">
                    {result.created.map((c, i) => (
                      <li key={i}>
                        {c.name} — {c.type.replace("_", " ")}
                        {c.generatedCount
                          ? ` · ${c.generatedCount} tasks`
                          : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] rounded-b-lg">
          {!result ? (
            <>
              <button
                type="button"
                onClick={() => handleClose(false)}
                disabled={importM.isPending}
                className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#334155] hover:bg-[#F1F5F9] disabled:opacity-50"
                data-testid={TID.importCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => importM.mutate()}
                disabled={!canSubmit}
                data-testid={TID.importSubmit}
                className="h-9 px-3.5 rounded-md bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B] disabled:opacity-50 inline-flex items-center gap-2"
              >
                {importM.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                Import
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={reset}
                className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#334155] hover:bg-[#F1F5F9]"
              >
                Import another
              </button>
              <button
                type="button"
                onClick={() => handleClose(false)}
                data-testid={TID.importDone}
                className="h-9 px-3.5 rounded-md bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B]"
              >
                Done
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultChip({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      className="ab-card px-3 py-2 flex items-center gap-2"
      style={{ borderColor: accent.bg }}
      data-testid={testId}
    >
      <div
        className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
        style={{ background: accent.bg, color: accent.fg }}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">
          {label}
        </div>
        <div className="text-base font-semibold text-[#0F172A] leading-tight tabular-nums">
          {value ?? 0}
        </div>
      </div>
    </div>
  );
}
